import { timestamp, Timestamp } from '@fullstory/proto/google/protobuf';
import { ConfigID, Facility, Notification } from '@fullstory/proto/fs/services/pinguin';
import {
  ChargeFailedId,
  NearQuotaId,
  ReachedQuotaId,
} from '@fullstory/client-api/goimports/fs_services_pinguin_templates_account';
import { intl } from '@fullstory/intl';
import { Role } from '@fullstory/proto/fs/services/orgs';

// The list of Account Management triggerIds that do not auto dismiss
export const userDismiss = [ChargeFailedId, NearQuotaId, ReachedQuotaId];
export const groupedConfig: ConfigID = {
  facility: Facility.FACILITY_INVALID,
  triggerId: 'GroupedNotification',
};

export const maxTimestamp = (
  first: timestamp | null | undefined,
  second: timestamp | null | undefined,
): timestamp | null => {
  if (!first && !second) return null;
  if (!first) return second ?? null;
  if (!second) return first;
  return first > second ? first : second;
};

export const isAutoDismissable = (notification: Notification, userRole: Role) => {
  const triggerId = notification.configId?.triggerId ?? '';
  const facility = notification.configId?.facility;

  return !(
    facility == Facility.ACCOUNT_MANAGEMENT &&
    (userRole === Role.ADMIN || userRole === Role.UMBRELLA_MANAGER) &&
    userDismiss.includes(triggerId)
  );
};

// returns a list of "notifications" to display as popovers,
// with the last one potentially a grouped notification
export const getNotificationsToDisplay = (
  notifications: Notification[],
  userRole: Role,
): Notification[] => {
  // non auto dismissable notifications should be sorted before dismissable notifications
  notifications.sort((a, b) => {
    if (isAutoDismissable(a, userRole)) return isAutoDismissable(b, userRole) ? 0 : 1;
    if (isAutoDismissable(b, userRole)) return -1;
    return 0;
  });

  // We only want up to 3 popovers if possible
  // Never group force dismissed popovers, and show these first
  // If there is still space, show dismissable popovers
  const forceDismissed = notifications.filter(n => !isAutoDismissable(n, userRole));
  const count = forceDismissed.length;

  return forceDismissed.concat(groupNotifications(notifications.slice(count), 3 - count));
};

// Takes in a list of notifications and a max number of items to return
// Will return max - 1 notifications followed by a grouped notification for the remaining
// If max <= 1, will return a single grouped notification
const groupNotifications = (notifications: Notification[], max: number): Notification[] => {
  // no notifications so return an empty list
  if (!notifications.length) return [];

  // if the list length is <= the max then return the whole list
  if (max >= notifications.length) {
    return notifications;
  }

  // we always want to return at least the grouped notification
  if (max <= 1) {
    return [buildGroupedNotification(notifications.length)];
  }

  // otherwise, return max-1 of the notifications followed by a grouped notification for the remaining
  const grouped = buildGroupedNotification(notifications.length - max + 1);
  return notifications.slice(0, max - 1).concat(grouped);
};

// Returns a fake "notification" that represents a group of the remaining unread notifications.
// Will be appended to the notification popover list
const buildGroupedNotification = (count: number): Notification => {
  const title =
    count > 10
      ? intl.formatMessage({ defaultMessage: 'You have 10+ other new notifications', id: 'R0traN' })
      : intl.formatMessage(
          {
            defaultMessage:
              'You have {count} other new {count, plural, one {notification} other {notifications}}',
            id: 'WxR5L0',
          },
          { count },
        );
  return {
    id: '1',
    configId: groupedConfig,
    createdAt: Timestamp.fromDate(new Date()),
    count: 1,
    botNotifications: [],
    read: false,
    content: {
      title,
      preheader: '',
      content: intl.formatMessage({
        defaultMessage: 'Click view to see all the new notifications.',
        id: 'mtEq8b',
      }),
      ctaLabel: intl.formatMessage({ defaultMessage: 'View', id: 'FgydNe' }),
      ctaUrl: '', // The Popover component will set an onclick to open the sidecar
    },
  };
};
